import { NovaSpinner } from "@nova/component-nova-spinner"
import classNames from "classnames"
import { supportedMimeTypes } from "features/job-management/uploader/constants"
import { HTMLAttributes, useRef } from "react"
import * as Icons from "@nova/component-icons"
import { useDropBox, UseDropBoxProps } from "./useDropBox"

export const AddMoreDropBox = ({
    canAcceptMultiple = false,
    allowedFileExtensions,
    onNewItems,
    isProcessing,
    horizontalPaddingInRem,
    title,
    hasProcessingError = false,
    iconOverride,
    ...rest
}: {
    innerClassName?: string
    horizontalPaddingInRem?: number
    maxFileSizeInMB?: number
    title?: string
    iconOverride?: Icons.IconComponent
} & UseDropBoxProps &
    HTMLAttributes<HTMLDivElement>) => {
    const {
        uploadRef,
        hasUploadError,
        canDrop,
        setCanDrop,
        processingText,
        handleInputChange,
        formattedFileTypes,
        isOver,
        dropRef,
    } = useDropBox({ canAcceptMultiple, allowedFileExtensions, onNewItems, isProcessing })

    const hasError = hasUploadError || hasProcessingError
    const ref = useRef(null)
    dropRef(ref)
    return (
        <div {...rest} className={classNames("select-none", rest.className)}>
            {!isProcessing ? (
                <div
                    ref={ref}
                    className={classNames(
                        "relative w-full py-2 border rounded-md flex gap-2 items-center justify-center group cursor-pointer",
                        {
                            "px-4": horizontalPaddingInRem === undefined,
                            "border-dashed": !hasError,
                            "border-red-500": hasError || (isOver && !canDrop),
                            "border-white-300 hover:border-white-1000": !hasError && !isOver,
                            "border-blue-500": isOver && canDrop,
                        }
                    )}
                    style={{
                        paddingLeft: horizontalPaddingInRem && horizontalPaddingInRem + "rem",
                        paddingRight: horizontalPaddingInRem && horizontalPaddingInRem + "rem",
                    }}
                    onDragOver={(e) => {
                        e.preventDefault()
                        const items = e.dataTransfer?.items

                        let canDrop = true

                        for (let i = 0; i < items.length && canDrop; i++) {
                            const { type } = items[i]

                            // type === "" when it is a .kmz or a .gpx
                            if (
                                items[i].kind !== "file" ||
                                (!supportedMimeTypes.includes(type) && type !== "")
                            )
                                canDrop = false
                        }

                        setCanDrop(canDrop)
                    }}
                    onDragLeave={() => setCanDrop(false)}
                    onClick={() => uploadRef.current?.click()}
                >
                    <Icons.Icon Icon={iconOverride || Icons.Backup} className="text-gray-0" />
                    <h5 className="group-hover:text-blue-500 text-gray-0">{title}</h5>

                    <input
                        ref={uploadRef}
                        type="file"
                        multiple={canAcceptMultiple}
                        accept={formattedFileTypes}
                        onChange={handleInputChange}
                        className="hidden"
                    />
                </div>
            ) : (
                <div className="flex gap-2.5 p-4 items-center">
                    <NovaSpinner width={24} height={24} />
                    <h4 className="text-gray-0">{processingText}</h4>
                </div>
            )}
        </div>
    )
}
